/* eslint-disable import/order */
// 🔩 Base
import React from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';

// 🇫🇷 i18n
import { injectIntl } from 'react-intl';
import { LocalizedMessage } from 'intl/LocalizedComponents';

// 🧱 Components
import DateTimeItem from './components/DateTimeItem';
import Popover from 'components/core/Popover';
import TimeSelector from './components/TimeSelector';
import AmPmSelector from './components/AmPmSelector';
import { Calendar } from '@material-ui/pickers';
import { ClearIconButton } from 'components/buttons/IconButtons';

// 🔧 Utils
import moment from 'moment';

// 🪝 Hooks
import useResponsive from 'hooks/useResponsive';
import useDateTimePicker from './useDateTimePicker';

// 🎨 Styles
import './DateTimePicker2.scss';

// 🧰 Dates configuration
const formatDate = (date, intl, formatOption) => (date instanceof Date ? new Intl.DateTimeFormat(intl.locale || intl.defaultLocale.locale, formatOption).format(date) : '');

const dateFormatOptions = {
  weekday: 'short',
  year: 'numeric',
  month: 'short',
  day: 'numeric',
};

const timeFormatOptions = {
  hour: '2-digit',
  minute: '2-digit',
};

const POPOVER_HORIZONTAL_OFFSET = '-30';

/* ------------------ */
/*     Component      */
/* ------------------ */
const DateTimePicker2 = ({
  name,
  calendarType,
  disabled,
  clearable,
  hideIcons,

  date,
  defaultDate,

  minDate,
  maxDate,

  beginDate,
  endDate,

  onChange,

  intl,
  labelDateId,
  labelHourId,
  placeholderId,
  calendarLabelId,
  errorText,
}) => {
  const buttonRef = React.useRef(null);
  const isMobile = useResponsive();

  const {
    /* Manage calendar */
    openCalendar,
    setOpenCalendar,
    handleResetDate,
    handleOpenCalendar,

    /* Manage selected date */
    selectedDate,
    setSelectedDate,
    handleSelectedDateChange,

    /* Manage selected time */
    handleSelectHour,
    OptionsHours,

    handleSelectMinute,
    OptionsMinutes,

    /* AM/PM Management */
    usesAmPm,
    canSelectAm,
    canSelectPm,
    timeRange,
    handleSelectTimeRange,

    /* Highlight period */
    renderDay,
  } = useDateTimePicker({
    calendarType,
    disabled,
    onChange,

    date,
    defaultDate,
    minDate,
    maxDate,

    beginDate,
    endDate,
  });

  return (
    <div className="d-flex flex-column align-items-center">
      {/* 🕰️ Displayed dates */}
      <div
        ref={buttonRef}
        className="d-flex mx-auto"
        style={{ cursor: (disabled ? 'default !important' : 'pointer !important'), opacity: (disabled ? 0.5 : 1) }}
        role="button"
        tabIndex="0"
      >
        {/* Date */}
        <DateTimeItem
          name={`${name}Date`}
          disabled={disabled}
          labelId={labelDateId}
          iconId={hideIcons ? null : 'btnBookings'}
          formattedDate={formatDate(date, intl, dateFormatOptions)}
          placeholderId={`${placeholderId}.date`}
          inputWidth={isMobile ? '150px' : '170px'}
          onClick={handleOpenCalendar}
        />
        {/* Time */}
        <DateTimeItem
          name={`${name}Time`}
          disabled={disabled}
          labelId={labelHourId || 'date.unit.hour'}
          iconId={hideIcons ? null : 'itemHour'}
          formattedDate={formatDate(date, intl, { ...timeFormatOptions, hour12: usesAmPm })}
          placeholderId={`${placeholderId}.hour`}
          inputWidth="70px"
          onClick={handleOpenCalendar}
        />
        {clearable ? <ClearIconButton onClick={handleResetDate} /> : null}
      </div>
      {/* ❌ Error */}
      {errorText ? <div className="error-text">{errorText}</div> : null}

      {/* 🪟 Popover */}
      <Popover
        open={!disabled && openCalendar}
        onClose={() => {
          setOpenCalendar(false);
          // If we close the pop over without validating, we reinit to the default date
          setSelectedDate(date || defaultDate);
        }}
        anchorEl={buttonRef.current}
        anchorOrigin={{
          vertical: 'center',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'center',
          horizontal: isMobile ? 0 : POPOVER_HORIZONTAL_OFFSET,
        }}
        style={{ transform: 'translateX(30px)' }}
      >
        <h5 className="px-4 my-3 text-center">{intl.formatMessage({ id: calendarLabelId })}</h5>
        <hr className="my-0" />
        {/* 🗓️ Calendar */}
        {/* ❗ We have to use moment adapter with Material UI Picker current version so each date passed must be a moment object (and conversely we "unmoment" the date in the onChange handler before giving it back to parent component) */}
        <div className="px-2">
          <Calendar
            date={moment(selectedDate || minDate)}
            onChange={handleSelectedDateChange}
            renderDay={renderDay}
            {...(minDate !== null && { minDate })}
            {...(maxDate !== null && { maxDate })}
          />
        </div>
        <hr className="my-3" />
        {/* 🕰️ Time */}
        <div className="d-flex justify-content-center align-items-center" style={{ fontSize: '20px', gap: '5px' }}>
          <TimeSelector
            selectedDate={selectedDate}
            OptionsHours={OptionsHours}
            handleSelectHour={handleSelectHour}
            OptionsMinutes={OptionsMinutes}
            handleSelectMinute={handleSelectMinute}
          />
          <AmPmSelector
            usesAmPm={usesAmPm}
            canSelectAm={canSelectAm}
            canSelectPm={canSelectPm}
            timeRange={timeRange}
            handleSelectTimeRange={handleSelectTimeRange}
          />
        </div>
        {/* ✅ Validate button */}
        <button
          className="mt-4 w-100 text-center p-2 validate-button"
          onClick={() => {
            setOpenCalendar(false);
            onChange(selectedDate);
          }}
          type="button"
        >
          <LocalizedMessage id="button.validate" />
        </button>
      </Popover>
    </div>
  );
};

DateTimePicker2.propTypes = {
  name: PropTypes.string.isRequired,
  calendarType: PropTypes.string,
  disabled: PropTypes.bool,
  clearable: PropTypes.bool,
  hideIcons: PropTypes.bool,

  calendarLabelId: PropTypes.string.isRequired,
  placeholderId: PropTypes.string.isRequired,

  labelDateId: PropTypes.string.isRequired,
  labelHourId: PropTypes.string,

  date: PropTypes.instanceOf(Date),
  defaultDate: PropTypes.instanceOf(Date),
  minDate: PropTypes.instanceOf(Date),
  maxDate: PropTypes.instanceOf(Date),

  beginDate: PropTypes.instanceOf(Date),
  endDate: PropTypes.instanceOf(Date),

  isRangePeriod: PropTypes.bool,

  onChange: PropTypes.func,
  intl: PropTypes.object.isRequired,
  errorText: PropTypes.string,
};

DateTimePicker2.defaultProps = {
  calendarType: null,
  disabled: false,
  clearable: false,
  hideIcons: false,

  labelHourId: null,
  isRangePeriod: false,

  onChange: null,
  date: null,
  defaultDate: new Date(),

  minDate: null,
  maxDate: null,

  beginDate: null,
  endDate: null,

  errorText: null,
};

module.exports = compose(
  injectIntl,
)(DateTimePicker2);



// WEBPACK FOOTER //
// ./components/core/DateTimePicker2/DateTimePicker2.js